/* eslint-disable react/jsx-boolean-value */
import React, { useContext, useEffect, useState } from 'react';
import ContextWatson from '../../../context/WatsonContext';
import Accordion, { AccordionItem } from '../../@base/Accordion';
import ComponentType from '../../Component/ComponentType';
import { showRemoveConfirmation as showDeleteConfirmation } from '../Commons';
import styles from './DialogNode.module.css';
import DialogNodeOutputsController from './DialogNodeOutputsController';
import { DialogBreadCrumbs } from './DialogNodeBreadCrumbs';
import { DialogConditionsController } from './DialogNodeConditionsController';
import DialogLocalizationItem from './DialogNodeLocalizationItem';
import SaveContext from '../../../context/SaveContext';
import DialogoNodeNextStepController from './DialogoNodeNextStepController';
import DialogoNodeSlotsController from './DialogoNodeSlotsController';
import DialogSlotsDTO from '../../../dto/DialogSlotsDTO';

/** Componente que representa Dialogo ou pasta do fluxo do watson */
const DialogNode = (props) => {
  const context = useContext(ContextWatson);
  const saveContext = useContext(SaveContext);

  const [title, setTitle] = useState('');
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedPreviousSibling, setSelectedPreviousSibling] = useState(null);
  const [dialogConditions, setDialogConditions] = useState(null);
  const [outputs, setOutputs] = useState(null);
  const [nextStep, setNextStep] = useState(null);
  const [slots, setSlots] = useState(null);
  const [dialogToDelete, setDialogToDelete] = useState(false);
  const [enabledChannels, setEnabledChannels] = useState([]);

  /** Retorna true se estiver salvando algo no backend */
  const isSaving = () => saveContext.savingStatus.isSaving;

  const sucessCallbackRemoveDialog = () => {
    setDialogToDelete([]);
    props.deselectDialog();
  };

  /** Mostra mensagem de confirmação de remorção do diálogo */
  const showRemoveConfirmation = () => {
    if (dialogToDelete.length > 0) {
      const confirmation = (d) => context.deleteDialog(d[0].id, sucessCallbackRemoveDialog);
      return showDeleteConfirmation(
        dialogToDelete,
        confirmation,
        setDialogToDelete,
      );
    }
    return null;
  };

  const isDialogAfolder = (
    props.selectedDialog.data.typeElement.id === ComponentType.WATSON_DIALOG_FOLDER.id);

  /** Carrega estados iniciais que alimentam os controles */
  const setInitialStates = () => {
    const { label } = props.selectedDialog.data;
    const { parent } = props.selectedDialog.data;
    const { previousSibling } = props.selectedDialog.data;
    const dialogConditionsLoaded = props.selectedDialog.data.dialogConditions;
    const outputsLoaded = props.selectedDialog.data.outputs;
    const nextStepLoaded = props.selectedDialog.data.nextStep;
    const slotsLoaded = props.selectedDialog.data.slots;
    const enabledChannelsLoaded = props.selectedDialog.data.enabledChannels;

    setTitle(label || '');
    setSelectedParent(parent || undefined);
    setSelectedPreviousSibling(previousSibling || '');
    setDialogConditions(dialogConditionsLoaded);
    setOutputs(outputsLoaded);
    setNextStep(nextStepLoaded);
    setSlots(slotsLoaded);
    setEnabledChannels(enabledChannelsLoaded);
  };

  const failedSaving = () => {
    setInitialStates();
  };

  const saveDialog = (
    dialogPartWillBeSave,
    recreate,
    sucessCallback,
  ) => {
    const dialogUpdated = {
      id: props.selectedDialog.id,
      title,
      dialogConditions,
      outputs,
      parent: props.selectedDialog.data.parent,
      previousSibling: props.selectedDialog.data.previousSibling,
      nextStep,
      slots,
      enabledChannels,
      ...dialogPartWillBeSave,
    };
    context.saveDialog(dialogUpdated,
      recreate,
      sucessCallback,
      failedSaving);
  };

  const sucessCallbackLocalizationChange = (typeItem, parent) => {
    if (typeItem === 'pasta') {
      props.openOrCloseFolders(parent, true);
    }
    props.setNodeOnFocus(props.selectedDialog.id);
  };

  /** Manipula ação de mudança da localização do dialogo
   *  Se typeItem for pasta, altera a pasta pai do nó corrente para o relatedNode
   *  Caso contrario, altera previousSibling para o relatedNode
   * @param typeItem pasta ou dialogo
   * @param relatedNode nó relacionado que parent e/ou previousSibling
  */
  const localizationItemChangeHandler = (typeItem, relatedNode) => {
    let parent = null;
    let previousSibling = null;

    if (typeItem === 'pasta') {
      parent = relatedNode.id;
      previousSibling = -1;
      setSelectedParent(relatedNode.id);
    } else {
      previousSibling = relatedNode.id;
      parent = relatedNode.parent;
      setSelectedPreviousSibling(relatedNode.id);
    }

    const recreate = !!parent && !isDialogAfolder;
    saveDialog({
      parent,
      previousSibling,
    }, recreate, () => sucessCallbackLocalizationChange(typeItem, relatedNode.id));
  };

  const enableDisableNextStep = () => {
    if (!nextStep) {
      setNextStep({
        dialog: '',
        selector: '',
      });
    } else {
      setNextStep(null);
      saveDialog({ nextStep: null }, true, null);
    }
  };

  const enableDisableSlots = () => {
    if (!slots) {
      setSlots([new DialogSlotsDTO()]);
    } else {
      setSlots(null);
      saveDialog({ slots: null }, false, null);
    }
  };

  const saveTitle = () => {
    saveDialog({ title: title.trim() }, false, null);
  };

  const saveConditions = (conditions) => {
    saveDialog({ dialogConditions: conditions }, false, null);
  };

  const saveSlots = (changedSlots) => {
    saveDialog({ slots: changedSlots }, false, null);
  };

  const saveOutputs = (changedOutputs, sucessCallback = null) => {
    saveDialog({ outputs: changedOutputs }, false, sucessCallback);
  };

  const saveNextStep = (changedNextStep) => {
    setNextStep(changedNextStep);
    if (changedNextStep.dialog !== '' && changedNextStep.selector !== '') {
      saveDialog({ nextStep: changedNextStep }, false, null);
    }
  };

  const saveEnabledChannels = (changedEnabledChannels) => {
    saveDialog({ enabledChannels: changedEnabledChannels }, false, null);
    setEnabledChannels(changedEnabledChannels);
  };

  /** Mostra detalhamento do dialogo */
  const showDialogDetail = () => (
    <>
      <DialogBreadCrumbs
        title={title}
        setTitle={setTitle}
        setDialogToDelete={setDialogToDelete}
        save={saveTitle}
        watsonNameInstance={props.watsonNameInstance}
        selectedDialog={props.selectedDialog}
        nextStepEnabled={!!nextStep}
        enableDisableNextStep={enableDisableNextStep}
        slotsEnabled={!!slots}
        enableDisableSlots={enableDisableSlots}
        channelsEnabled={enabledChannels}
        setChannelsEnabled={setEnabledChannels}
        saveEnabledChannels={saveEnabledChannels}
      />
      <Accordion classes={styles.DialogAccordion}>
        {!isDialogAfolder ? (
          <>
            <AccordionItem active>
              <span>Responde se:</span>
              <DialogConditionsController
                dialogConditions={dialogConditions}
                intents={context.intents}
                entities={context.entities}
                save={saveConditions}
                disabled={isSaving()}
                setDialogConditions={setDialogConditions}
              />

            </AccordionItem>

            {
              slots ? (
                <AccordionItem active>
                  <span>Antes de responder, coletar informações:</span>
                  <DialogoNodeSlotsController
                    slots={slots}
                    save={saveSlots}
                    setDialogSlots={setSlots}
                    disabled={isSaving()}
                  />
                </AccordionItem>
              ) : <></>
            }

            <AccordionItem active>
              <span>Respostas:</span>
              <DialogNodeOutputsController
                outputs={outputs}
                setOutputs={setOutputs}
                save={saveOutputs}
                disabled={isSaving()}
              />
            </AccordionItem>
            {nextStep ? (
              <AccordionItem active>
                <span>Seguir para:</span>
                <DialogoNodeNextStepController
                  selectedDialog={props.selectedDialog.id}
                  setNextStep={setNextStep}
                  nextStep={nextStep}
                  watsonNameInstance={props.watsonNameInstance}
                  save={saveNextStep}
                  disabled={isSaving()}
                />
              </AccordionItem>
            ) : <></>}
          </>
        ) : <></>}

        <AccordionItem>
          <span>Localização no fluxo</span>
          <div className={styles.ComponentSubList} style={{ textAlign: 'left' }}>
            {['pasta', 'depois'].map((item) => (
              <DialogLocalizationItem
                key={item}
                watsonNameInstance={props.watsonNameInstance}
                item={item}
                selectedDialog={props.selectedDialog}
                selectedParent={selectedParent}
                selectedPreviousSibling={selectedPreviousSibling}
                localizationItemChangeHandler={localizationItemChangeHandler}
                disabled={isSaving()}
              />
            ))}
          </div>
        </AccordionItem>
      </Accordion>
    </>
  );

  /** Efeito executado quando o componente é montado */
  useEffect(() => {
    props.setWidth('m5');
    props.setBoxOpened(true);
    props.setScroll(true);
    props.setWithBackdrop(false);
  }, []);

  /** Efeito executado quando o dialogo selecionado é alterado */
  useEffect(() => {
    props.setWithBackdrop(false);
    setInitialStates();
  }, [props.selectedDialog]);

  return (
    <>
      {showRemoveConfirmation()}
      {showDialogDetail()}
    </>
  );
};

export default DialogNode;
